import {
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { useField } from 'formik';
import InputWrapper from '../InputWrapper';

interface MultiSelectProps {
  name: string;
  label: string;
  isLabelMultiline?: boolean;
  disabled?: boolean;
  options: {
    value: string | boolean | number;
    label: string;
    category?: string;
  }[];
  tooltip?: string;
  required?: boolean;
}

function MultiCheckboxInput({
  name,
  label,
  isLabelMultiline,
  disabled,
  options,
  tooltip,
  required,
}: MultiSelectProps) {
  const [{ value }, , { setValue, setTouched }] = useField(name.toString());
  const selectedList = (value ?? []) as (string | number | boolean)[];

  const isAllSelected = options.every(option =>
    selectedList.includes(option.value),
  );

  const groupedOptions = options.reduce(
    (acc, option) => {
      const category = option.category ?? '';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(option);
      return acc;
    },
    {} as Record<string, { value: string | boolean | number; label: string }[]>,
  );

  return (
    <InputWrapper
      label={label}
      isLabelMultiline={isLabelMultiline}
      name={name}
      tooltip={tooltip}
      required={required}>
      <Stack width="100%" alignItems="start" gap={1.5}>
        <Button
          variant="link"
          onClick={() => {
            setValue(isAllSelected ? [] : options.map(option => option.value));
            setTimeout(() => setTouched(true), 0);
          }}>
          <Typography variant="inherit" fontWeight={600} color="text.primary">
            {isAllSelected ? 'Deselect All' : 'Select All'}
          </Typography>
        </Button>
        {Object.entries(groupedOptions).map(([category, categoryOptions]) => (
          <Stack key={category || 'no-category'} width="100%" gap={0.5}>
            {Object.keys(groupedOptions).length > 1 && (
              <Typography variant="h5" color="text.secondary">
                {category}:
              </Typography>
            )}
            {categoryOptions.map(option => (
              <FormControlLabel
                key={String(option.value)}
                sx={{
                  p: 0,
                  px: 1,
                }}
                control={
                  <Checkbox
                    checked={selectedList.includes(option.value)}
                    onChange={() => {
                      setValue(
                        selectedList.includes(option.value)
                          ? selectedList.filter(val => val !== option.value)
                          : [...selectedList, option.value],
                      );
                      setTimeout(() => setTouched(true), 0);
                    }}
                    disabled={disabled}
                    sx={{
                      p: 0,
                      mr: 1,
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.primary">
                    {option.label}
                  </Typography>
                }
                disabled={disabled}
              />
            ))}
          </Stack>
        ))}
      </Stack>
    </InputWrapper>
  );
}

export default MultiCheckboxInput;
